import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { AIEntityContext, AIResourceRef } from "@k8s-dashboard/contracts";
import { useAnnounce } from "@/app/Announcer";
import { useAsyncErrorFocus, useReturnFocus } from "@/app/focus";
import { useT } from "@/i18n/I18nProvider";
import { ActionCardList } from "./ActionCard";
import { useAgentChatStore } from "./AgentChatProvider";
import { ERROR_MESSAGE, PROGRESS_MESSAGE, REFERENCE_MESSAGE, ROLE_MESSAGE } from "./labels";
import { referenceHref } from "./referenceHref";

/* 링크 규칙은 referenceHref 하나입니다. 기존 테스트가 이 이름으로 부르므로 그대로
   다시 내보냅니다 — 규칙을 옮겼을 뿐 두 벌로 만들지 않습니다. */
export { referenceHref };

/**
 * AI Agent Chat Panel — ADR 0026 기능 4 · Issue #18 Phase A
 * --------------------------------------------------------------------------
 * Pod·Workload 상세에서 **화면이 이미 읽은 UID**를 그대로 들고 열립니다. URL의
 * 이름이 아닙니다 — 이름이 같아도 재생성된 Pod는 다른 인스턴스이기 때문입니다.
 *
 * 세 가지 규칙이 이 파일의 형태를 정합니다.
 *
 *   낭독은 하나  진행 상태만 중앙 Announcer로 갑니다. 답변 본문·tool 원문은
 *               어떤 live region에도 들어가지 않습니다. (ADR 0025)
 *   링크는 안전  답변 안의 리소스 참조는 **서버가 실제로 읽은 대상**뿐이고,
 *               계약 검증을 통과한 것만 내부 라우트로 렌더링합니다. 자유 URL도
 *               HTML/Markdown도 없습니다 — 답변은 텍스트로만 그려집니다.
 *   상태는 보존  자동 갱신과 SPA 이동이 입력·스크롤·세션을 지우지 않습니다.
 *
 * 여기에 없는 것도 계약입니다 — Action Card, 실행 버튼, tool 선택, 임의 질의.
 */

export interface ChatPanelProps {
  entity: AIEntityContext;
  /** 표시용 이름. 신원이 아니라 라벨입니다. */
  subjectName: string;
  subjectKind: string;
  /** 기능 스위치. false면 이 컴포넌트는 **아무것도 렌더링하지 않습니다**. */
  enabled: boolean;
}

export function ChatPanel({ entity, subjectName, subjectKind, enabled }: ChatPanelProps) {
  if (!enabled) return null;
  return <ChatPanelBody entity={entity} subjectName={subjectName} subjectKind={subjectKind} />;
}

function ChatPanelBody({ entity, subjectName, subjectKind }: Omit<ChatPanelProps, "enabled">) {
  const { t } = useT("agent");
  const [draft, setDraft] = useState("");
  const store = useAgentChatStore();
  const announce = useAnnounce();
  const { search } = useLocation();
  /* 열림 상태의 소유자도 저장소입니다 — 답변이 알려준 곳으로 이동했을 때 패널이
     닫혀 버리면, 이어서 물으라고 만든 흐름이 매번 클릭 하나를 요구합니다. */
  const open = store?.open ?? false;
  const setOpen = store?.setOpen;

  const headingId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const alert = useRef<HTMLDivElement>(null);

  /* 화면이 바뀌면 대화를 이어갈지 새로 시작할지 **저장소가** 정합니다.
     검증된 참조를 따라온 이동만 이어집니다(AgentChatProvider). */
  const bind = store?.bind;
  const entityKey = `${entity.clusterId}/${entity.namespace ?? ""}/${entity.podUid ?? ""}/${entity.workloadUid ?? ""}`;
  useEffect(() => {
    bind?.(entity);
    // entityKey가 신원의 전부입니다 — 객체 정체성으로 걸면 렌더마다 다시 붙습니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bind, entityKey]);

  const chat = store?.conversation;

  useReturnFocus(open);
  useAsyncErrorFocus(open ? (chat?.errorEpisode ?? null) : null, alert);

  /* 열면 제목으로 포커스를 옮깁니다 — 패널 안의 맥락을 먼저 읽고 입력으로 갑니다.
     **여는 동작**일 때만입니다. 이미 열린 채로 새 화면에 도착했을 때 포커스를
     훔치면 라우트 포커스 규약(ADR 0025)이 깨집니다. */
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) heading.current?.focus();
    wasOpen.current = open;
  }, [open]);

  /* 진행 상태만 낭독합니다. 같은 문장은 Announcer가 접습니다. */
  useEffect(() => {
    if (!open) return;
    if (chat?.step) announce("agent-progress", t("agent.progress.live", { status: t(PROGRESS_MESSAGE[chat.step]) }));
    else if (chat?.phase === "done") announce("agent-progress", t("agent.progress.done"));
  }, [announce, chat?.phase, chat?.step, open, t]);

  /* Provider가 없으면 패널을 그리지 않습니다 — 상태의 소유자가 없는 채로
     대화를 시작하면 이동 한 번에 사라집니다. */
  if (!store || !chat) return null;

  const busy = chat.phase === "streaming";
  const submit = () => {
    store.ask(entity, draft);
    setDraft("");
    field.current?.focus();
  };

  /* 트리거는 **항상 같은 DOM 노드**로 남습니다. 열 때 지웠다가 닫을 때 새로 만들면
     돌려줄 요소가 사라져 포커스가 body로 떨어지고, 스크린리더가 맥락을 잃습니다. */
  const disclosure = (
    <button
      ref={trigger}
      type="button"
      className="btn"
      data-testid="agent-open"
      aria-expanded={open}
      aria-controls={open ? `${headingId}-panel` : undefined}
      onClick={() => setOpen?.(!open)}
    >
      {open ? t("agent.close") : t("agent.open")}
    </button>
  );

  if (!open) return disclosure;

  return (
    <>
      {disclosure}
      <section
        id={`${headingId}-panel`}
        className="agent-panel"
        aria-labelledby={headingId}
        data-testid="agent-panel"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            setOpen?.(false);
          }
        }}
      >
        <header className="agent-panel__head">
          <h2 id={headingId} ref={heading} tabIndex={-1} className="agent-panel__title">
            {t("agent.title")}
          </h2>
          <p className="agent-panel__subtitle">{t("agent.subtitle")}</p>
          <p className="agent-panel__context" data-testid="agent-context">
            {t("agent.context", { kind: subjectKind, name: subjectName, namespace: entity.namespace ?? "-" })}
          </p>
        </header>

        {/* 기록 영역은 live region이 아닙니다. 스트리밍 낭독은 중앙 Announcer 하나뿐입니다. */}
        <ol className="agent-panel__log" aria-label={t("agent.transcript.aria")} aria-live="off" data-testid="agent-log">
          {chat.messages.length === 0 && <li className="agent-panel__empty">{t("agent.empty")}</li>}
          {chat.messages.map((message) => (
            <li key={message.id} className={`agent-msg agent-msg--${message.role}`} data-testid={`agent-msg-${message.role}`}>
              <span className="agent-msg__role">{t(ROLE_MESSAGE[message.role])}</span>
              {/* 텍스트로만 그립니다 — HTML도 Markdown도 해석하지 않습니다. */}
              <p className="agent-msg__body">{message.content}</p>
              {message.truncated && <p className="agent-msg__note">{t("agent.truncated")}</p>}
              <ReferenceList references={message.references} search={search} />
              {/* 제안 전용 카드입니다. 승인·실행 컨트롤은 Phase B에 존재하지 않습니다. (ADR 0027) */}
              <ActionCardList
                actions={message.actions}
                references={message.references}
                approvals={message.approvals}
                search={search}
              />
            </li>
          ))}
        </ol>

        {chat.error && (
          <div ref={alert} tabIndex={-1} role="alert" className="notice notice--warning" data-testid="agent-error">
            {t(ERROR_MESSAGE[chat.error], { retry: String(chat.retryAfterSeconds ?? 0) })}
            <button type="button" className="btn btn--sm" data-testid="agent-retry" onClick={() => store.retry(entity)}>
              {t("agent.retry")}
            </button>
          </div>
        )}

        <form
          className="agent-panel__form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label className="agent-panel__label" htmlFor={`${headingId}-input`}>
            {t("agent.input.label")}
          </label>
          <textarea
            id={`${headingId}-input`}
            ref={field}
            className="agent-panel__input"
            data-testid="agent-input"
            rows={2}
            value={draft}
            placeholder={t("agent.input.placeholder")}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <div className="row">
            <button type="submit" className="btn" data-testid="agent-send" disabled={busy || draft.trim() === ""}>
              {t("agent.send")}
            </button>
            {busy && (
              <button type="button" className="btn btn--sm" data-testid="agent-stop" onClick={store.stop}>
                {t("agent.stop")}
              </button>
            )}
            {/* 진행 표시는 눈으로 보는 것입니다. 낭독은 Announcer가 합니다. */}
            {chat.step && (
              <span className="agent-panel__status" aria-hidden="true" data-testid="agent-status">
                {t(PROGRESS_MESSAGE[chat.step])}
              </span>
            )}
          </div>
        </form>
      </section>
    </>
  );
}

/** 서버가 확인한 참조만 내부 딥링크가 됩니다. 검증에 실패한 참조는 사라집니다. */
function ReferenceList({ references, search }: { references?: AIResourceRef[]; search: string }) {
  const { t } = useT("agent");
  if (!references || references.length === 0) return null;
  return (
    <ul className="agent-msg__refs" aria-label={t("agent.references")} data-testid="agent-references">
      {references.map((reference) => (
        <li key={`${reference.kind}-${reference.uid}`}>
          <Link className="chip" to={referenceHref(reference, search)}>
            {reference.kind === "pod"
              ? t(REFERENCE_MESSAGE.pod, { name: reference.name })
              : t(REFERENCE_MESSAGE.workload, { kind: reference.workloadKind ?? "", name: reference.name })}
          </Link>
        </li>
      ))}
    </ul>
  );
}
